import React, { useEffect, useState } from 'react';
import { Form, Button, Row, Col, DatePicker, Spin } from 'antd';

import moment from 'moment';
import FloatingLabel from '../../../components/FloatingLabel/FloatingLabel';
import AccountItem from '../../../components/AccountItem/AccountItem';
import { ChevronDown, ChevronUp, ChevronRight, ChevronLeft } from '../../../assets/icons';

import { toastNotify } from '../../../utils/toastNotify';

import { downloadStatementService } from '../../../services/StatementsAPI';

const CustomDatePicker = ({ label, inputId, name, id, minimumDate, onChangeDate, rules, initialValue }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    initialValue && setSelectedDate(initialValue);
  }, [initialValue]);

  return (
    <FloatingLabel label={label} inputId={inputId} inputValue={selectedDate} alwaysSmall>
      <>
        <div id='calendar-anchor' />
        <Form.Item name={name} rules={rules}>
          <DatePicker
            id={id}
            defaultValue={initialValue ?? null}
            allowClear={false}
            suffixIcon={calendarOpen ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
            onOpenChange={() => setCalendarOpen(!calendarOpen)}
            nextIcon={<ChevronRight size={24} verticalAlign='1px' color='#1A1519' />}
            prevIcon={<ChevronLeft size={24} verticalAlign='1px' color='#1A1519' />}
            superNextIcon={false}
            superPrevIcon={false}
            showToday={false}
            format='MMMM DD, YYYY'
            value={selectedDate}
            onChange={(value) => {
              setSelectedDate(value);
              onChangeDate(value);
            }}
            getPopupContainer={() => document.getElementById('calendar-anchor')}
            inputReadOnly
            disabledDate={(d) => !d || (minimumDate && d.isBefore(minimumDate)) || d > moment().startOf('day')}
          />
        </Form.Item>
      </>
    </FloatingLabel>
  );
};

const AccountStatements = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [isLoading, setLoading] = useState(false);
  const [isAPICalled, setAPICalled] = useState(false);
  const [, setDownloading] = useState(false);

  const [isFileExist, setFileExist] = useState(false);
  const [fileResponse, setFileResponse] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState({
    startDate: null,
    endDate: null,
  });

  const onDownloadStatement = async () => {
    if (!fileResponse) {
      toastNotify('error', 'Sorry, Could not download file!');
      return;
    }
    try {
      setDownloading(true);

      const blob = new Blob([fileResponse.data], { type: 'application/pdf;charset=utf-8;' });
      setFileExist(true);

      const url = window.URL.createObjectURL(blob);
      const [, filename] = fileResponse.headers['content-disposition']?.split('filename=');

      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', filename ?? 'Account-Statement.pdf');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      const formattedError = JSON.parse(String.fromCharCode.apply(null, Array.from(new Uint8Array(error?.response?.data))));
      toastNotify('error', formattedError?.response?.data?.message ?? 'Something went wrong!');
    } finally {
      setDownloading(false);
    }
  };

  const onApplyClick = async () => {
    try {
      setLoading(true);
      const fromDate = moment(startDate).utc(true).toISOString();
      const toDate = moment(endDate).utc(true).toISOString();
      const response = await downloadStatementService({
        fromDate,
        toDate,
      });

      setSelectedDuration({
        startDate: fromDate,
        endDate: endDate,
      });
      setFileResponse(response);
      setFileExist(true);
    } catch (error) {
      setFileResponse(null);
      setSelectedDuration({
        startDate: null,
        endDate: null,
      });
      setFileExist(false);
      const formattedError = JSON.parse(String.fromCharCode.apply(null, Array.from(new Uint8Array(error?.response?.data))));
      toastNotify('error', formattedError?.message ?? 'Something went wrong!');
    } finally {
      setLoading(false);
      setAPICalled(true);
    }
  };

  return (
    <>
      <div className='data__header'>
        <h4>Account Statements</h4>
        {/* <p className='cursor-pointer filter-trigger' onClick={() => setTaxDrawer(true)}>
          <Filter verticalAlign='1px' /> <span className='fw-600'>Filters</span> <span>(1)</span>{' '}
        </p> */}
        <p className='data__header__description'>Please select start date and end date to generate account statement.</p>
      </div>
      <div className=''>
        <Form>
          <Row gutter={16}>
            <Col span={24} sm={8}>
              <CustomDatePicker
                id={'startDate'}
                inputId={'startDate'}
                label={'Start Date'}
                name={'startDate'}
                onChangeDate={(val) => setStartDate(moment(val).startOf('day'))}
                rules={[
                  {
                    required: true,
                    message: 'Please select date',
                  },
                  {
                    message: 'Start Date can not be greater than End Date!',
                    validator: (_, value) => {
                      if (value && endDate) {
                        if (moment(value).isAfter(moment(endDate))) {
                          return Promise.reject('invalid date');
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Col>
            <Col span={24} sm={8}>
              <CustomDatePicker
                id={'endDate'}
                inputId={'endDate'}
                label={'End Date'}
                minimumDate={startDate}
                name={'endDate'}
                onChangeDate={(val) => setEndDate(moment(val).endOf('day'))}
                rules={[
                  {
                    required: true,
                    message: 'Please select date',
                  },
                  {
                    message: 'End Date is before start Date',
                    validator: (_, value) => {
                      if (value && startDate) {
                        if (moment(value).isBefore(moment(startDate))) {
                          return Promise.reject('invalid date');
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Col>
            <Col span={12} sm={8}>
              <Button
                htmlType='button'
                disabled={!startDate || !endDate || moment(endDate).isBefore(moment(startDate))}
                loading={isLoading}
                onClick={onApplyClick}
                className='ant-btn--tiny'
                type='primary'
              >
                Apply
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      <div className='data__items mb-24'>
        <Spin spinning={isLoading}>
          {isFileExist ? (
            <>
              <div className='data__items__head'>
                <p className='subhead-3'>Date Posted</p>
                <p className='subhead-3'>Statement</p>
              </div>
              <AccountItem
                date={moment().format('MMM DD, YYYY')}
                statement={`${moment(selectedDuration.startDate).format('MMM DD, YYYY')} - ${moment(selectedDuration.endDate).format(
                  'MMM DD, YYYY'
                )}`}
                action={onDownloadStatement}
              />
            </>
          ) : (
            <>
              {!isLoading && !isFileExist && (
                <div className='nodata'>
                  {isAPICalled && <p className='subhead-3 mb-4'>No account statements found.</p>}
                  <p className='mb-0'>All account statements will appear here once available.</p>
                </div>
              )}
            </>
          )}
        </Spin>
      </div>
    </>
  );
};

export default AccountStatements;
